// hooks/useAzureStorage.js - VERSIÓN CORREGIDA CON MEJOR MANEJO DE ERRORES
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { getAzureStorageContent, uploadFileToAzureStorage } from '../services/apiService';

// Query Keys Factory
export const azureStorageKeys = {
  all: ['azureStorage'],
  content: (path) => [...azureStorageKeys.all, 'content', path],
  projectManagement: () => [...azureStorageKeys.all, 'project-management']
};

// Configuración Azure
const AZURE_CONFIG = {
  BASE_DOMAIN: 'fcnfiles.file.core.windows.net',
  BASE_URL: 'https://fcnfiles.file.core.windows.net/appsfiles',
  SHARE_NAME: 'appsfiles',
  PROJECT_BASE: '7-GESTION_DE_PROYECTOS'
};

const PROJECT_PATHS = {
  ROOT: '',
  CONTRATOS: '/7.1-CONTRATOS',
  CONVENIOS: '/7.2-CONVENIOS', 
  DEPOSITOS: '/7.3-DEPOSITOS',
  EMERGENCIAS: '/7.4-EMERGENCIAS',
  ORDEN_DE_TRABAJO: '/7.5-ORDEN_DE_TRABAJO',
  EVALUACION: '/7.6-EVALUACION'
};

const ALLOWED_FILE_TYPES = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt', 
  '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.zip', '.rar'
];

// ✅ FUNCIÓN CORREGIDA: Evitar duplicación de rutas
const buildFileUrl = (folderPath, fileName) => {
  if (!fileName) return null;
  
  // Limpiar la ruta
  let cleanPath = '';
  if (folderPath) {
    cleanPath = folderPath.replace(/^\/+|\/+$/g, '');
    
    // ✅ IMPORTANTE: Verificar si ya incluye PROJECT_BASE para evitar duplicación
    if (!cleanPath.startsWith(AZURE_CONFIG.PROJECT_BASE)) {
      cleanPath = `${AZURE_CONFIG.PROJECT_BASE}/${cleanPath}`;
    }
  } else {
    cleanPath = AZURE_CONFIG.PROJECT_BASE;
  }
  
  const cleanFileName = encodeURIComponent(fileName.trim());
  
  // Construir URL sin duplicar PROJECT_BASE
  return `${AZURE_CONFIG.BASE_URL}/${cleanPath}/${cleanFileName}`;
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

// ✅ Hook principal CORREGIDO
export const useAzureStorage = (basePath = '') => {
  const queryClient = useQueryClient();

  const {
    data: folderContent = [],
    isLoading: loading,
    error,
    isFetching,
    isSuccess,
    refetch: fetchContent
  } = useQuery({
    queryKey: azureStorageKeys.content(basePath),
    queryFn: async () => {
      console.log(`📂 Cargando contenido de: ${AZURE_CONFIG.PROJECT_BASE}${basePath || ''}`);
      
      const response = await getAzureStorageContent(basePath);
      
      if (!response?.success) {
        throw new Error('Error al cargar contenido de Azure Storage');
      }
      
      const content = response.contenido || [];
      console.log(`✅ Contenido cargado: ${content.length} elementos`);
      
      return content.map(item => {
        return {
          ...item,
          id: `${basePath || 'root'}_${item.nombre}`,
          isDirectory: item.tipo === 'directory',
          isFile: item.tipo === 'file',
          extension: item.tipo === 'file' ? 
            item.nombre.substring(item.nombre.lastIndexOf('.')).toLowerCase() : null,
          sizeFormatted: item.tamaño ? formatFileSize(item.tamaño) : null,
          fullPath: `${basePath}/${item.nombre}`,
          url: item.url || buildFileUrl(basePath, item.nombre),
          downloadUrl: item.url || buildFileUrl(basePath, item.nombre),
          thumbnailUrl: item.url || buildFileUrl(basePath, item.nombre),
          parentPath: basePath,
          icon: item.tipo === 'directory' ? '📁' : getFileIcon(item.extension),
          isImage: item.tipo === 'file' && isImageFile(item.nombre)
        };
      });
    },
    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true
  });

  // ✅ Mutación CORREGIDA para subir múltiples archivos
  const uploadMultipleFilesMutation = useMutation({
    mutationFn: async ({ archivos, folderPrefix = null }) => {
      if (!Array.isArray(archivos) || archivos.length === 0) {
        throw new Error('Debe proporcionar al menos un archivo');
      }

      console.log(`📤 Subiendo ${archivos.length} archivos...`);

      const results = [];
      const errors = [];

      // Subir archivos secuencialmente
      for (let i = 0; i < archivos.length; i++) {
        const archivo = archivos[i];
        
        try {
          const validation = validateFile(archivo);
          if (!validation.isValid) {
            throw new Error(`${validation.errors.join(', ')}`);
          }

          // ✅ CORRECCIÓN: Simplificar nombres únicos
          const timestamp = Date.now();
          const randomId = Math.random().toString(36).substr(2, 5);
          const extension = archivo.name.substring(archivo.name.lastIndexOf('.'));
          const baseName = archivo.name.substring(0, archivo.name.lastIndexOf('.'));
          
          const uniqueName = folderPrefix 
            ? `${folderPrefix}_${timestamp}_${randomId}${extension}`
            : `${baseName}_${timestamp}_${randomId}${extension}`;

          const uniqueFile = new File([archivo], uniqueName, { type: archivo.type });
          
          console.log(`⬆️ Subiendo ${i + 1}/${archivos.length}: ${uniqueName}`);
          
          // ✅ CORRECCIÓN CRÍTICA: Mejor manejo de la respuesta
          const response = await uploadFileToAzureStorage(basePath, uniqueFile);
          
          console.log('📦 Respuesta del servidor:', response);
          
          // ✅ VALIDAR ESTRUCTURA DE RESPUESTA
          if (!response) {
            throw new Error('Respuesta vacía del servidor');
          }
          
          // El backend puede retornar diferentes estructuras
          // Intentar extraer la info de múltiples formas
          let uploadedFileName, folderPath, fileUrl;
          
          if (response.success === false) {
            throw new Error(response.message || 'Error al subir archivo');
          }
          
          // Caso 1: Response tiene la estructura {success, nombres, folderPath}
          if (response.nombres && Array.isArray(response.nombres)) {
            uploadedFileName = response.nombres[0] || uniqueName;
            folderPath = response.folderPath || basePath;
          }
          // Caso 2: Response tiene directamente el nombre del archivo
          else if (response.nombre) {
            uploadedFileName = response.nombre;
            folderPath = response.folderPath || basePath;
          }
          // Caso 3: Response es solo un string (el nombre del archivo)
          else if (typeof response === 'string') {
            uploadedFileName = response;
            folderPath = basePath;
          }
          // Caso 4: Usar el nombre único generado
          else {
            uploadedFileName = uniqueName;
            folderPath = basePath;
          }
          
          // Si el servidor ya proporcionó una URL, usarla
          if (response.url) {
            fileUrl = response.url;
          } else {
            // Construir la URL manualmente
            fileUrl = buildFileUrl(folderPath, uploadedFileName);
          }

          const fileResult = {
            success: true,
            originalName: archivo.name,
            uploadedName: uploadedFileName,
            fileName: uploadedFileName,
            url: fileUrl,
            cloudUrl: fileUrl,
            downloadUrl: fileUrl,
            size: archivo.size,
            sizeFormatted: formatFileSize(archivo.size),
            extension: extension.toLowerCase(),
            isImage: isImageFile(uploadedFileName),
            timestamp: new Date().toISOString(),
            index: i,
            folderPath: folderPath,
            backendResponse: response
          };

          results.push(fileResult);
          console.log(`✅ Archivo ${i + 1}/${archivos.length} subido exitosamente`);
          console.log(`   URL: ${fileUrl}`);

          // Delay entre subidas
          if (i < archivos.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }

        } catch (error) {
          console.error(`❌ Error subiendo archivo ${archivo.name}:`, error);
          console.error('   Detalle del error:', error.message);
          errors.push({
            fileName: archivo.name,
            error: error.message,
            index: i
          });
        }
      }

      // ✅ Arrays para la base de datos
      const urlsArray = results.map(r => r.url).filter(Boolean);
      const namesArray = results.map(r => r.uploadedName).filter(Boolean);
      
      console.log(`📋 RESUMEN DE SUBIDA:`);
      console.log(`  - Total archivos: ${archivos.length}`);
      console.log(`  - Subidos exitosamente: ${results.length}`);
      console.log(`  - Errores: ${errors.length}`);
      console.log(`  - URLs generadas:`, urlsArray);

      return {
        success: errors.length === 0,
        totalFiles: archivos.length,
        uploadedFiles: results.length,
        failedFiles: errors.length,
        results,
        errors,
        urlsArray: urlsArray,
        namesArray: namesArray,
        urlsString: urlsArray.join(','),
        namesString: namesArray.join(','),
        message: `${results.length}/${archivos.length} archivos subidos correctamente`
      };
    },
    onSuccess: (response) => {
      console.log(`✅ Subida múltiple completada: ${response.uploadedFiles}/${response.totalFiles} archivos`);
      queryClient.invalidateQueries({ queryKey: azureStorageKeys.content(basePath) });
    },
    onError: (error) => {
      console.error('❌ Error en subida múltiple:', error.message);
    }
  });

  // Función para parsear URLs de anexos
  const parseAnexoUrls = useCallback((anexoString) => {
    if (!anexoString || typeof anexoString !== 'string') {
      return [];
    }
    
    try {
      const urls = anexoString
        .split(',')
        .map(url => url.trim())
        .filter(url => url.length > 0);
      
      return urls.map((url, index) => {
        const fileName = url.includes('/')
          ? decodeURIComponent(url.substring(url.lastIndexOf('/') + 1))
          : 'archivo';
        
        const extension = fileName.includes('.')
          ? fileName.substring(fileName.lastIndexOf('.')).toLowerCase()
          : '';

        const isImage = isImageFile(fileName);
        
        return {
          id: `anexo_${index}_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
          url: url,
          downloadUrl: url,
          thumbnailUrl: isImage ? url : null,
          fileName: fileName,
          extension: extension,
          icon: getFileIcon(extension),
          isImage: isImage,
          isPdf: extension === '.pdf',
          isDocument: ['.doc', '.docx', '.xls', '.xlsx', '.txt'].includes(extension),
          index: index
        };
      });
      
    } catch (error) {
      console.error('❌ Error parseando anexo string:', error);
      return [];
    }
  }, []);

  // Función para agregar archivos al anexo existente
  const addFilesToAnexo = useCallback(async (archivosNuevos, anexoExistente = '', folderPrefix = null) => {
    try {
      console.log('📄 Agregando archivos al anexo existente...');
      
      const uploadResult = await uploadMultipleFilesMutation.mutateAsync({
        archivos: archivosNuevos,
        folderPrefix
      });

      if (!uploadResult.success) {
        throw new Error('Error al subir algunos archivos');
      }

      // Combinar URLs
      const urlsExistentes = anexoExistente 
        ? anexoExistente.split(',').map(url => url.trim()).filter(Boolean)
        : [];

      const urlsNuevas = uploadResult.urlsArray || [];
      
      const urlsCombinadas = [...new Set([...urlsExistentes, ...urlsNuevas])];
      const nuevoAnexoString = urlsCombinadas.join(',');

      console.log(`✅ Anexo actualizado:`);
      console.log(`  - Existentes: ${urlsExistentes.length}`);
      console.log(`  - Nuevas: ${urlsNuevas.length}`);
      console.log(`  - Total: ${urlsCombinadas.length}`);

      return {
        success: true,
        anexoString: nuevoAnexoString,
        urlsAgregadas: urlsNuevas,
        urlsExistentes: urlsExistentes,
        totalUrls: urlsCombinadas.length,
        uploadDetails: uploadResult,
        message: `${urlsNuevas.length} archivo(s) agregado(s) exitosamente`
      };

    } catch (error) {
      console.error('❌ Error agregando archivos al anexo:', error);
      return {
        success: false,
        error: error.message,
        anexoString: anexoExistente
      };
    }
  }, [uploadMultipleFilesMutation]);

  // Función de validación
  const validateFile = useCallback((archivo) => {
    const errors = [];
    
    if (!archivo) {
      errors.push('Debe seleccionar un archivo');
      return { isValid: false, errors };
    }

    const extension = archivo.name.substring(archivo.name.lastIndexOf('.')).toLowerCase();
    if (!ALLOWED_FILE_TYPES.includes(extension)) {
      errors.push(`Tipo de archivo no permitido: ${extension}`);
    }

    const MAX_SIZE = 50 * 1024 * 1024; // 50MB
    if (archivo.size > MAX_SIZE) {
      errors.push('El archivo es demasiado grande (máximo 50MB)');
    }

    if (archivo.name.length > 255) {
      errors.push('El nombre del archivo es demasiado largo');
    }

    const invalidChars = /[<>:"/\\|?*]/;
    if (invalidChars.test(archivo.name)) {
      errors.push('El nombre del archivo contiene caracteres no permitidos');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }, []);

  // Funciones de utilidad
  const getDirectories = useCallback(() => {
    return folderContent.filter(item => item.isDirectory);
  }, [folderContent]);

  const getFiles = useCallback(() => {
    return folderContent.filter(item => item.isFile);
  }, [folderContent]);

  const getImages = useCallback(() => {
    return folderContent.filter(item => item.isImage);
  }, [folderContent]);

  const getFilesByExtension = useCallback((extension) => {
    const targetExt = extension.startsWith('.') ? extension : `.${extension}`;
    return folderContent.filter(item => 
      item.isFile && item.extension === targetExt.toLowerCase()
    );
  }, [folderContent]);

  // Función wrapper para subir múltiples archivos
  const uploadMultipleFiles = useCallback(async (archivos, folderPrefix = null) => {
    try {
      const result = await uploadMultipleFilesMutation.mutateAsync({ 
        archivos, 
        folderPrefix 
      });
      return result;
    } catch (error) {
      return { 
        success: false, 
        error: error.message,
        totalFiles: archivos?.length || 0,
        uploadedFiles: 0,
        failedFiles: archivos?.length || 0
      };
    }
  }, [uploadMultipleFilesMutation]);

  // Estadísticas
  const stats = useMemo(() => {
    const directories = getDirectories();
    const files = getFiles();
    const images = getImages();
    const totalSize = files.reduce((total, file) => total + (file.tamaño || 0), 0);
    
    const filesByExtension = files.reduce((acc, file) => {
      const ext = file.extension || 'unknown';
      acc[ext] = (acc[ext] || 0) + 1;
      return acc;
    }, {});

    return {
      totalElements: folderContent.length,
      totalDirectories: directories.length,
      totalFiles: files.length,
      totalImages: images.length,
      totalSize,
      totalSizeFormatted: formatFileSize(totalSize),
      filesByExtension,
      hasContent: folderContent.length > 0,
      isEmpty: folderContent.length === 0
    };
  }, [folderContent, getDirectories, getFiles, getImages]);

  return {
    // Datos principales
    folderContent,
    directories: getDirectories(),
    files: getFiles(),
    images: getImages(),
    
    // Estados
    loading,
    error: error?.message || null,
    isFetching,
    isSuccess,
    isUploadingMultiple: uploadMultipleFilesMutation.isPending,
    multipleUploadError: uploadMultipleFilesMutation.error?.message || null,
    
    // Funciones de subida
    uploadMultipleFiles,
    
    // Funciones para anexos
    addFilesToAnexo,
    parseAnexoUrls,
    
    // Funciones de utilidad
    fetchContent,
    getFilesByExtension,
    validateFile,
    
    // Estadísticas y configuración
    stats,
    basePath,
    fullPath: `${AZURE_CONFIG.PROJECT_BASE}${basePath}`,
    AZURE_CONFIG,
    PROJECT_PATHS,
    ALLOWED_FILE_TYPES
  };
};

// Funciones auxiliares
function isImageFile(fileName) {
  if (!fileName) return false;
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
  const extension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
  return imageExtensions.includes(extension);
}

export const getFileIcon = (extension) => {
  if (!extension) return '📎';
  
  const iconMap = {
    '.pdf': '📄', '.doc': '📝', '.docx': '📝', '.txt': '📋',
    '.xls': '📊', '.xlsx': '📊',
    '.jpg': '🖼️', '.jpeg': '🖼️', '.png': '🖼️', '.gif': '🖼️', '.bmp': '🖼️', '.webp': '🖼️',
    '.zip': '🗜️', '.rar': '🗜️'
  };
  
  return iconMap[extension?.toLowerCase()] || '📎';
};

// Hooks específicos
export const useEvaluacionStorage = () => useAzureStorage(PROJECT_PATHS.EVALUACION);

export default useAzureStorage;